import React, { useState, useEffect } from 'react';
import { usePropsContext } from '@uhg-abyss/web/hooks/usePropsContext';
import PropTypes from 'prop-types';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { Badge } from '@uhg-abyss/web/ui/Badge';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import { ProviderTableData } from '../../../common/ProviderTableData';
import { ProviderTableSearch } from '../../../common/ProviderTableSearch';
import {
  StyledAccordionContent,
  StyledAccordionTrigger,
  StyledAccordionHeader,
  StyledTableRow,
  StyledTableDataCheckMarkColumn,
  StyledTableDataLabelColumn,
  StyledTableDataBadgeColumn,
} from '../../../common/StyledComponents/StyledComponents';

const corporatesTableSearchOptions = [
  { value: 'All', label: 'All' },
  { value: '3rd-Party Access', label: '3rd-Party Access' },
];

const corporatesColWidth = ['10%', '60%', '30%'];

const errorMessage = 'Unable to load corporate tax ID owner';

export const Corporates = ({
  corporates,
  selectedProvider,
  setSelectedProvider,
  setAllowProviderSearch,
  handleNewCorporateSelection,
  setExpandedValue,
  isCorporatesLoading,
  setIsOwnerRowClicked,
  isOwnerAccordionExpanded,
  setIsSaveDisabled,
  setIsCareProviderAccordionDisabled,
  setIsTaxIdAccordionDisabled,
  setCanCallProviders,
  isAccordionDisabled,
}) => {
  const { events } = usePropsContext();
  const {
    corporateProviderName,
    corporateProviderMpin,
    organizationId,
    uniqueId,
  } = selectedProvider;
  const [ownerSearchValue, setOwnerSearchValue] = useState('');
  const [ownerAccessFilter, setOwnerAccessFilter] = useState('All');
  const [selectedOwner, setSelectedOwner] = useState(
    `${corporateProviderName} (${corporateProviderMpin})`
  );
  const [selectedOwnerRow, setSelectedOwnerRow] = useState(uniqueId);

  useEffect(() => {
    if (!corporateProviderName) {
      setSelectedOwner('');
    }
    if (!uniqueId) {
      setSelectedOwnerRow('');
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (!selectedOwner) {
      setIsTaxIdAccordionDisabled(true);
      setIsCareProviderAccordionDisabled(true);
    } else {
      setIsTaxIdAccordionDisabled(false);
      setIsCareProviderAccordionDisabled(false);
    }
  }, [selectedOwner]);

  const filteredCorporatesData = corporates.filter(
    (item) =>
      item.label.toLowerCase().includes(ownerSearchValue.toLowerCase()) &&
      (ownerAccessFilter === 'All' ||
        (ownerAccessFilter === '3rd-Party Access' && item.isThirdParty))
  );

  let subText = '';
  if (!corporates && !isOwnerAccordionExpanded) {
    subText = errorMessage;
  } else {
    subText = selectedOwner;
  }

  const handleCorpOwnerChange = (selectedItem) => {
    const newCorporateProviderMpin = selectedItem.corpMpin || null;
    let newCorpOwner;
    if (newCorporateProviderMpin) {
      // uniqueId generated from mpin and id, so we're guaranteed to select corporates accurately where mpins may be the same
      newCorpOwner = (corporates || []).find(
        (corp) => corp.uniqueId === selectedItem.uniqueId
      );
    }
    if (selectedProvider.uniqueId !== newCorpOwner.uniqueId) {
      const newData = {
        ...(organizationId && {
          organizationId,
        }),
        corporateProviderLastName: newCorpOwner.lastName,
        corporateProviderName: newCorpOwner.name,
        corporateProviderMpin: newCorpOwner.corpMpin,
        ia: newCorpOwner.ia,
        paa: newCorpOwner.paa,
        ...(newCorpOwner.userId && {
          userId: newCorpOwner.userId,
        }),
        ...(newCorpOwner.uhcProviderId && {
          uhcProviderId: newCorpOwner.uhcProviderId,
        }),
        ...(newCorpOwner.uniqueId && {
          uniqueId: newCorpOwner.uniqueId,
        }),
        ...(newCorpOwner.isThirdParty && {
          isThirdParty: newCorpOwner.isThirdParty,
        }),
      };
      setSelectedProvider(newData);
      handleNewCorporateSelection(newData);
      events.onCorporateChange(newData);
    }
    setAllowProviderSearch(true);
  };

  const handleOwnerRowClick = (item) => {
    if (selectedOwnerRow === item.uniqueId) {
      return;
    }
    setIsOwnerRowClicked(true);
    setSelectedOwnerRow(item.uniqueId);
    setSelectedOwner(item.label);
    setExpandedValue('tax-id-number');
    handleCorpOwnerChange(item);
    setOwnerSearchValue('');
    setOwnerAccessFilter('All');
    setIsSaveDisabled(true);
    setIsCareProviderAccordionDisabled(true);
    setCanCallProviders(false);
  };

  const renderRow = (rowData) => {
    return rowData.map((item) => {
      return (
        <React.Fragment>
          <StyledTableRow
            key={item.uniqueId}
            onClick={() => handleOwnerRowClick(item)}
          >
            <StyledTableDataCheckMarkColumn>
              <If condition={selectedOwnerRow === item.uniqueId}>
                <IconMaterial size='22px' icon='check' />
              </If>
            </StyledTableDataCheckMarkColumn>
            <StyledTableDataLabelColumn
              data-testid='corporate-taxid-table-row'
              isSelectedRow={selectedOwnerRow === item.uniqueId}
            >
              {item.label}
            </StyledTableDataLabelColumn>
            <StyledTableDataBadgeColumn data-testid='corporate-taxid-access-badge'>
              <If condition={item.isThirdParty}>
                <Badge variant='info'>
                  <StyledTableDataBadgeColumn>
                    3rd-Party Access
                  </StyledTableDataBadgeColumn>
                </Badge>
              </If>
            </StyledTableDataBadgeColumn>
          </StyledTableRow>
        </React.Fragment>
      );
    });
  };

  return (
    <React.Fragment>
      <StyledAccordionTrigger isDisabled={isAccordionDisabled}>
        <Layout.Group>
          <If condition={!corporates && !isOwnerAccordionExpanded}>
            <IconSymbol icon='error' color='$error1' variant='outlined' />
          </If>
          <StyledAccordionHeader isError={!corporates} subText={subText}>
            Corporate tax ID owner
          </StyledAccordionHeader>
        </Layout.Group>
      </StyledAccordionTrigger>
      <Accordion.Content>
        <StyledAccordionContent>
          <ProviderTableSearch
            searchValue={ownerSearchValue}
            setSearchValue={setOwnerSearchValue}
            searchFilter={ownerAccessFilter}
            setSearchFilter={setOwnerAccessFilter}
            options={corporatesTableSearchOptions}
            isDisabled={isCorporatesLoading}
            isError={!corporates}
          />
          <ProviderTableData
            colWidths={corporatesColWidth}
            data={filteredCorporatesData}
            renderRow={renderRow}
            isLoading={isCorporatesLoading}
            ariaLoadingLabel='Loading corporate tax ID owners'
            isError={!corporates}
            errorMessage={errorMessage}
          />
        </StyledAccordionContent>
      </Accordion.Content>
    </React.Fragment>
  );
};

Corporates.propTypes = {
  corporates: PropTypes.arrayOf(PropTypes.shape({})),
  selectedProvider: PropTypes.shape({
    organizationId: PropTypes.string,
    corporateProviderMpin: PropTypes.string,
    id: PropTypes.string,
    uniqueId: PropTypes.string,
    corporateProviderName: PropTypes.string,
  }),
  setSelectedProvider: PropTypes.func.isRequired,
  setAllowProviderSearch: PropTypes.func.isRequired,
  handleNewCorporateSelection: PropTypes.func.isRequired,
  setExpandedValue: PropTypes.func.isRequired,
  isCorporatesLoading: PropTypes.bool.isRequired,
  setIsOwnerRowClicked: PropTypes.func.isRequired,
  isOwnerAccordionExpanded: PropTypes.bool.isRequired,
  setIsSaveDisabled: PropTypes.func.isRequired,
  setIsCareProviderAccordionDisabled: PropTypes.func,
  setCanCallProviders: PropTypes.func.isRequired,
  isAccordionDisabled: PropTypes.bool,
  setIsTaxIdAccordionDisabled: PropTypes.func,
};

Corporates.defaultProps = {
  corporates: [],
  selectedProvider: null,
  setIsCareProviderAccordionDisabled: () => {},
  isAccordionDisabled: false,
  setIsTaxIdAccordionDisabled: () => {},
};
